import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <kr-header> — behaviour for the custom header.
 *
 * Four jobs, all of them cheap:
 *   1. Sticky mode "scroll-up": hide on scroll down, reveal on scroll up.
 *   2. Mobile drawer: open/close, focus trap, scroll lock.
 *   3. Dismiss the CSS `:focus-within` mega menu on Escape or an outside click.
 *   4. Publish `--header-height` / `--header-group-height`, which Horizon's cart
 *      drawer and search modal position against.
 *
 * Scroll position comes from `@theme/scroll-container`, never `window.scrollY`: above
 * 990px Horizon's squeeze layout gives `html`/`body` `overflow: hidden` and makes
 * `.page-wrapper` the scroll container, so the window never scrolls and `scrollY` is
 * permanently 0. The container also swaps at that breakpoint, hence the rebind below.
 *
 * The scroll handler reads once per animation frame and only writes a data attribute,
 * so it never forces a synchronous layout.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class KrHeader extends HTMLElement {
  #lastY = 0;
  #ticking = false;
  #rafId = null;
  #lastFocused = null;
  #resizeObserver = null;
  #scrollTarget = null;
  #headerHeight = 0;

  connectedCallback() {
    this.drawer = this.querySelector('[data-kr-drawer]');
    this.openButton = this.querySelector('[data-kr-drawer-open]');
    this.closeButtons = this.querySelectorAll('[data-kr-drawer-close]');

    this.openButton?.addEventListener('click', () => this.openDrawer());
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeDrawer()));

    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);

    this.#publishHeights();
    this.#resizeObserver = new ResizeObserver(() => this.#publishHeights());
    this.#resizeObserver.observe(this);

    if (this.dataset.sticky === 'scroll-up') {
      this.#lastY = getScrollTop();
      this.#scrollTarget = getScrollEventTarget();
      this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
      scrollContainerMediaQuery.addEventListener('change', this.#onBreakpointChange);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
    scrollContainerMediaQuery.removeEventListener('change', this.#onBreakpointChange);
    this.#resizeObserver?.disconnect();
    // No-op unless this header owned the lock — matters when the theme editor tears the
    // section down while the drawer is open, which would otherwise strand the page locked.
    unlockScroll(this);

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * Crossing 990px swaps the scroll container between `.page-wrapper` and the document,
   * so the cached binding goes stale. Reseeding `#lastY` from the new container stops the
   * first post-resize event comparing against a position from the old one, which would
   * otherwise register as a large scroll down and hide the header spuriously.
   */
  #onBreakpointChange = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#lastY = getScrollTop();
  };

  /**
   * Horizon's cart drawer / search modal offset themselves by these vars. The stock
   * header sets them from `layout/theme.liquid`; this custom header replaces it, so it
   * has to keep them accurate itself.
   */
  #publishHeights() {
    const height = this.offsetHeight;
    const group = document.getElementById('header-group');
    let groupHeight = height;

    if (group) {
      groupHeight = 0;
      for (const child of group.children) {
        if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
      }
    }

    // Cached so the scroll handler stays free of layout reads.
    this.#headerHeight = height;

    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
  }

  /* --- Drawer ------------------------------------------------------------ */

  openDrawer() {
    if (!this.drawer) return;
    this.#lastFocused = document.activeElement;
    // A hidden (translated) header would become the containing block for the fixed-position
    // drawer, so pin it for as long as the drawer is open.
    this.dataset.scrollState = 'pinned';
    this.drawer.hidden = false;
    // Next frame so the transition has a start state to animate from.
    requestAnimationFrame(() => this.drawer.setAttribute('data-open', 'true'));
    // Horizon's refcounted lock, not an inline overflow: it sets `html[scroll-lock]`,
    // which base.css applies to both `html` and `.page-wrapper` (the desktop scroll
    // container), and it composes with the cart drawer instead of fighting it.
    lockScroll(this);
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.drawer.querySelector(FOCUSABLE)?.focus();
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    this.drawer.removeAttribute('data-open');
    unlockScroll(this);
    this.openButton?.setAttribute('aria-expanded', 'false');

    const finish = () => {
      this.drawer.hidden = true;
    };
    // Wait for the slide-out so the panel isn't yanked out of the DOM mid-animation.
    this.drawer.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 400);

    this.#lastFocused?.focus?.();
  }

  #trapFocus(event) {
    const focusable = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* --- Events ------------------------------------------------------------ */

  #onKeydown = (event) => {
    const drawerOpen = this.drawer && !this.drawer.hidden;

    if (event.key === 'Escape') {
      if (drawerOpen) {
        this.closeDrawer();
        return;
      }
      this.#closeMegaMenu();
      return;
    }

    if (event.key === 'Tab' && drawerOpen) this.#trapFocus(event);
  };

  #onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.#closeMegaMenu();
  };

  /**
   * The mega menu is opened purely by `:hover` / `:focus-within`, so "closing" it means
   * moving focus out of the nav. Nothing to do when focus is already elsewhere.
   */
  #closeMegaMenu() {
    const nav = this.querySelector('.kr-header__nav');
    if (nav?.contains(document.activeElement)) document.activeElement.blur();
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;

    this.#rafId = requestAnimationFrame(() => {
      const y = getScrollTop();
      const threshold = this.#headerHeight || this.offsetHeight;

      if (y <= threshold) {
        this.dataset.scrollState = 'top';
      } else {
        this.dataset.scrollState = y > this.#lastY ? 'hidden' : 'pinned';
      }

      this.#lastY = y;
      this.#ticking = false;
      this.#rafId = null;
    });
  };
}

if (!customElements.get('kr-header')) {
  customElements.define('kr-header', KrHeader);
}
